import { HHStack } from '@hinge-health/react-component-library';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Navigate, Route, Routes } from 'react-router-dom';
import { UnbilledReport } from './components/bills/unbilled/unbilled-report';
import BillingToolHeader from './components/headers/billing-tool-header';
import { RequireBillingWrite } from './components/require-billing-write';
import { routes } from './constants/strings/routes';
import { BillingProvider } from './context/billing.context';
import { DynamicDialogProvider } from './context/dynamic-dialog.context';
import { SnackbarProvider } from './context/snackbar.context';
import BillManagement from './pages/bill-management';
import BillToolRunPage from './pages/bill-tool-run';
import BillingToolHomePage from './pages/billing-tool-home';
import ClientByIdPage from './pages/client-id';
import ClientInsurerBatchContractCreate from './pages/client-insurer-batch-create';
import ClientInsurerBatchContractCreateStatus from './pages/client-insurer-batch-create-status';
import ClientInsurerByIdPage from './pages/client-insurer-id';
import ClientsInsurerIdEditContractPackage from './pages/client-insurer-id-edit-contract-package';
import ClientInsurerNewContractPackage from './pages/client-insurer-id-new-contract-package';
import ClientInsurerIndexPage from './pages/client-insurer-index';
import BillingCsvUpload from './pages/csv-upload';
import CvsFeeSchedules from './pages/cvs-fee-schedules';
import DiscountCalculator from './pages/discount-calculator';
import EligibilityVerifier from './pages/eligibility-verifier';
import EsiFeeSchedules from './pages/esi-fee-schedules';
import FeeSchedules from './pages/fee-schedules';
import HoldsAggregate from './pages/holds-aggregate';
import IcdCodes from './pages/icd-codes';
import Packages from './pages/packages';
import ProcedureCodes from './pages/procedure-codes';
import ProgramCapBatchImportPage from './pages/program-cap-batch-import';
import ProgramCapsPage from './pages/program-caps';
import RevenueShares from './pages/revenue-shares';
import UserByIdPage from './pages/user-id';
import VolumeBasedDiscounts from './pages/volume-based-discounts';

export const BillingTool = (): JSX.Element => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <BillingProvider>
      <SnackbarProvider>
        <DynamicDialogProvider>
          <HHStack
            display="flex"
            flexDirection="column"
            height="100%"
            minWidth="0"
            sx={{ backgroundColor: '#fff' }}
          >
            <BillingToolHeader />
            <Routes>
              <Route index element={<BillingToolHomePage />} />
              <Route path={routes.billing.clientInsurerPath}>
                <Route index element={<ClientInsurerIndexPage />} />
                <Route
                  path={routes.billing.clientInsurerId}
                  element={<ClientInsurerByIdPage />}
                />
                <Route
                  path={routes.billing.fullBatchContractCreateStatus}
                  element={<ClientInsurerBatchContractCreateStatus />}
                />
                <Route element={<RequireBillingWrite />}>
                  <Route
                    path={routes.billing.fullNewContractPackagePath}
                    element={<ClientInsurerNewContractPackage />}
                  />
                  <Route
                    path={routes.billing.fullEditContractPackagePath}
                    element={<ClientsInsurerIdEditContractPackage />}
                  />
                  <Route
                    path={routes.billing.batchContractCreate}
                    element={<ClientInsurerBatchContractCreate />}
                  />
                </Route>
              </Route>
              <Route path={routes.billing.clientPath}>
                <Route index element={<Navigate to={routes.billing.home} />} />
                <Route
                  path={routes.billing.clientId}
                  element={<ClientByIdPage />}
                />
              </Route>
              <Route path={routes.billing.userPath}>
                <Route index element={<Navigate to={routes.billing.home} />} />
                <Route path={routes.billing.userId}>
                  <Route index element={<Navigate to={'subscriptions'} />} />
                  <Route path="subscriptions" element={<UserByIdPage />} />
                  <Route path="engagements" element={<UserByIdPage />} />
                  <Route path="fee-for-service" element={<UserByIdPage />} />
                  <Route path="admin-fee-bills" element={<UserByIdPage />} />
                </Route>
              </Route>
              <Route
                path={routes.billing.billManagement}
                element={<BillManagement />}
              />
              <Route
                path={routes.billing.submissionsManagement}
                element={<BillManagement />}
              />
              <Route
                path={routes.billing.unbilledReport}
                element={<UnbilledReport />}
              />
              <Route
                path={routes.billing.holdsAggregate}
                element={<HoldsAggregate />}
              />
              <Route
                path={routes.billing.discountCalculator}
                element={<DiscountCalculator />}
              />
              <Route element={<RequireBillingWrite />}>
                <Route path="tool" element={<BillToolRunPage />} />
                <Route
                  path={routes.billing.batchProgramCapImport}
                  element={<ProgramCapBatchImportPage />}
                />
                <Route
                  path={routes.billing.csvUpload}
                  element={<BillingCsvUpload />}
                />
              </Route>
              <Route
                path={routes.billing.programCaps}
                element={<ProgramCapsPage />}
              />
              <Route
                path={routes.billing.clientFeeSchedules}
                element={<FeeSchedules />}
              />
              <Route
                path={routes.billing.partnershipFeeSchedules}
                element={<FeeSchedules />}
              />
              <Route
                path={routes.billing.esiFeeSchedules}
                element={<EsiFeeSchedules />}
              />
              <Route
                path={routes.billing.cvsFeeSchedules}
                element={<CvsFeeSchedules />}
              />
              <Route
                path={routes.billing.procedureCodes}
                element={<ProcedureCodes />}
              />
              <Route path={routes.billing.icdCodes} element={<IcdCodes />} />
              <Route path={routes.billing.packages} element={<Packages />} />
              <Route
                path={routes.billing.revenueShares}
                element={<RevenueShares />}
              />
              <Route
                path={routes.billing.volumeBasedDiscounts}
                element={<VolumeBasedDiscounts />}
              />
              {/* Read-only verification tool — deliberately outside RequireBillingWrite. */}
              <Route
                path={routes.billing.eligibilityVerifier}
                element={<EligibilityVerifier />}
              />
              <Route path="*" element={<Navigate to={routes.billing.home} />} />
            </Routes>
          </HHStack>
        </DynamicDialogProvider>
      </SnackbarProvider>
    </BillingProvider>
  </LocalizationProvider>
);
